import { useState, useEffect } from 'react'
import { useGoals, useDebts, useAccounts } from '../hooks/useData'
import { useAuth } from '../hooks/useAuth'
import { GoldButton, GhostButton, Modal, Input, Select, fmt, colors, fonts, Loading } from '../components/UI'
import { containsSensitiveData, SENSITIVE_DATA_ERROR } from '../lib/sensitiveDataCheck'
import BillLogo from '../components/BillLogo'
import { getPayCycle } from '../lib/payday'
import Hint from '../components/Hint'

const GOAL_COLORS = ['#3db87a', '#8b5cf6', '#4a8fe8', '#e8b84b', '#ec4899']
const GOAL_ICONS  = ['💰', '🏖️', '🏠', '✈️', '🎯', '🌱', '📱']
const CELEBRATED_KEY = 'tic_goals_celebrated_v1'

function getCelebrated() {
  try { return new Set(JSON.parse(localStorage.getItem(CELEBRATED_KEY) || '[]')) } catch { return new Set() }
}
function markCelebrated(id) {
  try {
    const s = getCelebrated(); s.add(id)
    localStorage.setItem(CELEBRATED_KEY, JSON.stringify([...s]))
  } catch {}
}

function ProgressBar({ pct, color }) {
  return (
    <div style={{ height: '6px', background: 'rgba(255,255,255,0.07)', borderRadius: '3px', overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${Math.min(100, Math.max(0, pct))}%`, background: color, borderRadius: '3px', transition: 'width 0.5s' }} />
    </div>
  )
}

function IC({ emoji, color, size = 48 }) {
  return (
    <div style={{ width: size, height: size, borderRadius: '50%', background: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: Math.round(size * 0.5), flexShrink: 0 }}>
      {emoji}
    </div>
  )
}

function Toast({ message, onDismiss }) {
  useEffect(() => { const t = setTimeout(onDismiss, 4000); return () => clearTimeout(t) }, [onDismiss])
  return (
    <div style={{ position: 'fixed', bottom: '96px', left: '50%', transform: 'translateX(-50%)', zIndex: 300, background: colors.green, color: '#fff', fontFamily: fonts.heading, fontWeight: 700, fontSize: '15px', padding: '12px 24px', borderRadius: '24px', boxShadow: '0 4px 20px rgba(61,184,122,0.5)', whiteSpace: 'nowrap' }}>
      {message}
    </div>
  )
}

export default function GoalsPage({ onNavigate, basicMode = false }) {
  const { goals, loading: gLoad, upsertGoal, deleteGoal } = useGoals()
  const { debts, loading: dLoad, upsertDebt, deleteDebt } = useDebts()
  const { accounts, upsertAccount, refresh: refreshAccounts } = useAccounts()
  const creditCards = accounts.filter(a => a.type === 'credit_card')

  const [showPayModal, setShowPayModal] = useState(false)
  const [payingCard, setPayingCard] = useState(null)
  const [payForm, setPayForm] = useState({ amount: '', account_id: '', clear_all: true })
  const [payingSaving, setPayingSaving] = useState(false)

  async function handlePayStatement() {
    if (!payingCard) return
    setPayingSaving(true)
    const amount = payForm.clear_all ? (payingCard.balance || 0) : parseFloat(payForm.amount) || 0
    if (amount <= 0) { setPayingSaving(false); return }
    await upsertAccount({ ...payingCard, balance: Math.max(0, (payingCard.balance || 0) - amount) })
    if (payForm.account_id) {
      const payFrom = accounts.find(a => a.id === payForm.account_id)
      if (payFrom) await upsertAccount({ ...payFrom, balance: (payFrom.balance || 0) - amount })
    }
    await refreshAccounts()
    setPayingSaving(false)
    setShowPayModal(false)
    setPayingCard(null)
  }

  function openPayModal(card) {
    setPayingCard(card)
    const currentAccounts = accounts.filter(a => a.type === 'current')
    setPayForm({ amount: String(card.balance || ''), account_id: currentAccounts[0]?.id || '', clear_all: true })
    setShowPayModal(true)
  }

  const [showGoalModal, setShowGoalModal] = useState(false)
  const [editingGoal, setEditingGoal] = useState(null)
  const [goalForm, setGoalForm] = useState({ name: '', target_amount: '', current_amount: '', monthly_contribution: '', linked_account_id: '' })
  const [savingGoal, setSavingGoal] = useState(false)
  const [goalError, setGoalError] = useState('')

  const [showDebtModal, setShowDebtModal] = useState(false)
  const [editingDebt, setEditingDebt] = useState(null)
  const [debtForm, setDebtForm] = useState({ name: '', lender: '', original_balance: '', current_balance: '', apr: '', monthly_payment: '' })
  const [savingDebt, setSavingDebt] = useState(false)
  const [debtError, setDebtError] = useState('')

  const [toast, setToast] = useState(null)
  const [showArchived, setShowArchived] = useState(false)

  // Check for newly completed goals and fire celebration toast
  useEffect(() => {
    if (!goals.length) return
    const celebrated = getCelebrated()
    goals.forEach(g => {
      if (!g.is_archived && g.target_amount > 0 && g.current_amount >= g.target_amount && !celebrated.has(g.id)) {
        setToast(`🎉 ${g.name} goal reached!`)
        markCelebrated(g.id)
      }
    })
  }, [goals])

  function openAddGoal() {
    setEditingGoal(null)
    setGoalForm({ name: '', target_amount: '', current_amount: '', monthly_contribution: '', linked_account_id: '', source_account_id: '' })
    setGoalError(''); setShowGoalModal(true)
  }

  function openEditGoal(g) {
    setEditingGoal(g)
    setGoalForm({
      name: g.name,
      target_amount: String(g.target_amount || ''),
      current_amount: String(g.current_amount || ''),
      monthly_contribution: String(g.monthly_contribution || ''),
      linked_account_id: g.linked_account_id || '',
      source_account_id: g.source_account_id || '',
    })
    setGoalError(''); setShowGoalModal(true)
  }

  async function handleSaveGoal() {
    if (containsSensitiveData(goalForm.name)) { setGoalError(SENSITIVE_DATA_ERROR); return }
    setSavingGoal(true); setGoalError('')
    // If linked account, use account balance as current amount
    const linkedAccount = accounts.find(a => a.id === goalForm.linked_account_id)
    const currentAmount = linkedAccount ? linkedAccount.balance : (parseFloat(goalForm.current_amount) || 0)
    await upsertGoal({
      id: editingGoal?.id,
      name: goalForm.name,
      target_amount: parseFloat(goalForm.target_amount) || 0,
      current_amount: currentAmount,
      monthly_contribution: parseFloat(goalForm.monthly_contribution) || 0,
      linked_account_id: goalForm.linked_account_id || null,
      source_account_id: goalForm.source_account_id || null,
      is_archived: editingGoal?.is_archived || false,
    })
    setSavingGoal(false); setShowGoalModal(false)
  }

  async function handleArchiveGoal() {
    if (!editingGoal) return
    await upsertGoal({ ...editingGoal, is_archived: true })
    setShowGoalModal(false)
  }

  function openAddDebt() { setEditingDebt(null); setDebtForm({ name: '', lender: '', original_balance: '', current_balance: '', apr: '', monthly_payment: '' }); setDebtError(''); setShowDebtModal(true) }
  function openEditDebt(d) { setEditingDebt(d); setDebtForm({ name: d.name, lender: d.lender || '', original_balance: String(d.original_balance || ''), current_balance: String(d.current_balance || ''), apr: String(d.apr || ''), monthly_payment: String(d.monthly_payment || '') }); setDebtError(''); setShowDebtModal(true) }
  async function handleSaveDebt() {
    if (containsSensitiveData(debtForm.name)) { setDebtError(SENSITIVE_DATA_ERROR); return }
    setSavingDebt(true); setDebtError('')
    await upsertDebt({ id: editingDebt?.id, name: debtForm.name, lender: debtForm.lender, original_balance: parseFloat(debtForm.original_balance) || 0, current_balance: parseFloat(debtForm.current_balance) || 0, apr: parseFloat(debtForm.apr) || 0, monthly_payment: parseFloat(debtForm.monthly_payment) || 0 })
    setSavingDebt(false); setShowDebtModal(false)
  }

  const activeGoals = goals.filter(g => !g.is_archived)
  const archivedGoals = goals.filter(g => g.is_archived)
  const completedGoals = activeGoals.filter(g => g.target_amount > 0 && g.current_amount >= g.target_amount)
  const incompleteGoals = activeGoals.filter(g => !(g.target_amount > 0 && g.current_amount >= g.target_amount))

  // Skip this month - per goal per cycle, stored in localStorage
  const { profile } = useAuth()
  const payDay = profile?.pay_day
  const shiftWeekends = profile?.pay_day_shift_weekends !== false
  const currentCycle = payDay ? getPayCycle(payDay, new Date(), shiftWeekends) : null
  const cycleKey = currentCycle?.startIso || new Date().toISOString().slice(0, 7)

  function isSkipped(goalId) {
    try { return !!localStorage.getItem(`tic_goal_skipped_${goalId}_${cycleKey}`) } catch { return false }
  }
  function toggleSkip(goalId, e) {
    e.stopPropagation()
    try {
      const key = `tic_goal_skipped_${goalId}_${cycleKey}`
      if (localStorage.getItem(key)) localStorage.removeItem(key)
      else localStorage.setItem(key, '1')
      setSkipVersion(v => v + 1)
    } catch {}
  }
  const [skipVersion, setSkipVersion] = useState(0)

  const totalSaved = activeGoals.reduce((s, g) => s + (g.current_amount || 0), 0)
  const totalTarget = activeGoals.reduce((s, g) => s + (g.target_amount || 0), 0)
  const overallPct = totalTarget > 0 ? Math.round((totalSaved / totalTarget) * 100) : 0
  const monthlyContrib = activeGoals.reduce((s, g) => s + (g.monthly_contribution || 0), 0)
  const activeDebts = debts.filter(d => !d.is_paid_off)

  // Savings & investment accounts
  const savingsAccounts = accounts.filter(a => ['savings', 'isa', 'investments', 'pension'].includes(a.type) || a.is_emergency_fund)

  const accountOptions = [{ value: '', label: 'No linked account' }, ...accounts.map(a => ({ value: a.id, label: `${a.name} (${fmt(a.balance)})` }))]

  if (gLoad || dLoad) return <Loading />

  return (
    <div style={{ paddingBottom: '32px' }}>
      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '18px 20px 4px', position: 'relative' }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '20px', color: colors.text }}>Goals</div>
        </div>
        <button onClick={openAddGoal} style={{ position: 'absolute', right: '16px', width: '32px', height: '32px', borderRadius: '50%', background: 'transparent', border: `2px solid ${colors.gold}`, color: colors.gold, fontSize: '20px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>+</button>
      </div>

      <div style={{ padding: '14px 16px 0' }}>

        {/* Goals overview */}
        {(activeGoals.length > 0 || activeDebts.length > 0) && (
          <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', padding: '16px', backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur, marginBottom: '20px' }}>
            <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.gold, marginBottom: '14px' }}>GOALS OVERVIEW</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1px 1fr 1px 1fr', gap: 0 }}>
              <div style={{ paddingRight: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginBottom: '4px' }}>Total saved</div>
                <div style={{ fontFamily: fonts.heading, fontSize: '22px', fontWeight: 800, color: colors.green, lineHeight: 1 }}>{fmt(totalSaved)}</div>
                <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '3px' }}>of {fmt(totalTarget)} target</div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.06)' }} />
              <div style={{ padding: '0 12px' }}>
                <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginBottom: '4px' }}>Overall progress</div>
                <div style={{ fontFamily: fonts.heading, fontSize: '22px', fontWeight: 800, color: colors.text, lineHeight: 1 }}>{overallPct}%</div>
                <div style={{ marginTop: '8px' }}><ProgressBar pct={overallPct} color={colors.green} /></div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.06)' }} />
              <div style={{ paddingLeft: '12px' }}>
                <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginBottom: '4px' }}>Monthly contribution</div>
                <div style={{ fontFamily: fonts.heading, fontSize: '22px', fontWeight: 800, color: colors.text, lineHeight: 1 }}>{fmt(monthlyContrib)}</div>
                <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '3px' }}>across {activeGoals.length} goals</div>
              </div>
            </div>
          </div>
        )}

        {/* Savings accounts summary */}
        {savingsAccounts.length > 0 && (
          <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', overflow: 'hidden', backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur, marginBottom: '14px' }}>
            <div style={{ padding: '14px 16px 10px', fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text }}>SAVINGS & INVESTMENT ACCOUNTS</div>
            {savingsAccounts.map((acc) => {
              const linkedGoal = activeGoals.find(g => g.linked_account_id === acc.id)
              return (
                <div key={acc.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <BillLogo name={acc.name} category={acc.type} size={40} radius={20} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '15px', fontWeight: 600, color: colors.text }}>{acc.name}</div>
                    <div style={{ fontSize: '12px', color: colors.muted, marginTop: '2px' }}>{acc.type === 'isa' ? 'ISA' : acc.type ? acc.type.charAt(0).toUpperCase() + acc.type.slice(1) : ''}{acc.is_emergency_fund ? ' · Emergency fund' : ''}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontFamily: fonts.heading, fontWeight: 700, fontSize: '16px', color: colors.green }}>{fmt(acc.balance)}</div>
                    <div style={{ fontSize: '12px', color: colors.mutedDeep, marginTop: '2px' }}>{linkedGoal ? linkedGoal.name : 'No target set'}</div>
                  </div>
                </div>
              )
            })}
            <div style={{ padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '13px', color: colors.muted }}>Total in savings:</span>
              <span style={{ fontSize: '13px', fontWeight: 700, color: colors.green }}>{fmt(savingsAccounts.reduce((s, a) => s + (a.balance || 0), 0))}</span>
            </div>
          </div>
        )}

        {/* Savings goals */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text }}>SAVINGS & INVESTMENT GOALS</div>
        </div>

        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', overflow: 'hidden', backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur, marginBottom: '10px' }}>
          {incompleteGoals.length === 0 && completedGoals.length === 0 ? (
            <div style={{ padding: '28px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', marginBottom: '10px' }}>💰</div>
              <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, lineHeight: 1.6 }}>No savings goals yet. Add one to start tracking your progress.</div>
            </div>
          ) : (
            <>
              {incompleteGoals.map((goal, i) => {
                const pct = goal.target_amount > 0 ? Math.round((goal.current_amount / goal.target_amount) * 100) : 0
                const c = GOAL_COLORS[i % GOAL_COLORS.length]
                const icon = goal.is_emergency_fund ? '🛡️' : GOAL_ICONS[i % GOAL_ICONS.length]
                const skipped = isSkipped(goal.id)
                return (
                  <div key={goal.id} onClick={() => openEditGoal(goal)} style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '10px' }}>
                      <IC emoji={icon} color={c} size={48} />
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <div style={{ fontSize: '15px', fontWeight: 700, color: colors.text }}>{goal.name}</div>
                          {skipped && (
                            <div style={{ fontSize: '11px', fontWeight: 700, color: colors.muted, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '2px 7px' }}>Skipped</div>
                          )}
                        </div>
                        <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '2px' }}>
                          {goal.monthly_contribution > 0 ? `${fmt(goal.monthly_contribution)}/mo contribution` : ''}
                        </div>
                      </div>
                      <div style={{ fontFamily: fonts.heading, fontSize: '24px', fontWeight: 800, color: c }}>{pct}%</div>
                      <div style={{ fontSize: '16px', color: `rgba(${colors.textRgb},0.3)` }}>›</div>
                    </div>
                    <ProgressBar pct={pct} color={c} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted }}>{fmt(goal.current_amount || 0)} saved</div>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted }}>{fmt(goal.target_amount || 0)} target</div>
                    </div>
                    {goal.monthly_contribution > 0 && (
                      <button
                        onClick={e => toggleSkip(goal.id, e)}
                        style={{
                          marginTop: '10px', width: '100%', padding: '8px',
                          background: skipped ? 'rgba(255,255,255,0.05)' : 'rgba(201,151,43,0.08)',
                          border: `1px dashed ${skipped ? 'rgba(255,255,255,0.15)' : 'rgba(201,151,43,0.3)'}`,
                          borderRadius: '8px', color: skipped ? colors.muted : colors.gold,
                          fontFamily: fonts.body, fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                        }}
                      >
                        {skipped ? '↩ Un-skip this month' : '⏭ Skip this month'}
                        <Hint title="Skip this month" text="Removes this goal's contribution from Safe to Spend for this period only. The auto-transfer won't run either. Resets automatically next period." />
                      </button>
                    )}
                  </div>
                )
              })}

              {/* Completed goals */}
              {completedGoals.map((goal, i) => (
                <div key={goal.id} onClick={() => openEditGoal(goal)} style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer', background: 'rgba(61,184,122,0.04)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                    <IC emoji="🏆" color={colors.green} size={48} />
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ fontSize: '15px', fontWeight: 700, color: colors.text }}>{goal.name}</div>
                        <div style={{ fontSize: '11px', fontWeight: 700, color: colors.green, background: 'rgba(61,184,122,0.15)', border: '1px solid rgba(61,184,122,0.3)', borderRadius: '6px', padding: '2px 7px' }}>Reached</div>
                      </div>
                      <div style={{ fontSize: '13px', fontWeight: 500, color: colors.green, marginTop: '2px' }}>{fmt(goal.current_amount)} saved</div>
                    </div>
                    <div style={{ fontSize: '16px', color: `rgba(${colors.textRgb},0.3)` }}>›</div>
                  </div>
                </div>
              ))}
            </>
          )}
          <div style={{ padding: '12px 16px' }}>
            <button onClick={openAddGoal} style={{ width: '100%', padding: '12px', background: 'transparent', border: '1px dashed rgba(201,151,43,0.35)', borderRadius: '10px', color: colors.gold, fontFamily: fonts.body, fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}>
              + Add savings goal
            </button>
          </div>
        </div>

        {/* Archived goals */}
        {archivedGoals.length > 0 && (
          <div style={{ marginBottom: '14px' }}>
            <button onClick={() => setShowArchived(v => !v)} style={{ background: 'none', border: 'none', color: colors.muted, fontSize: '13px', fontWeight: 600, cursor: 'pointer', padding: '4px 0', display: 'flex', alignItems: 'center', gap: '6px' }}>
              {showArchived ? '▾' : '▸'} {archivedGoals.length} archived goal{archivedGoals.length !== 1 ? 's' : ''}
            </button>
            {showArchived && (
              <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', overflow: 'hidden', backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur, marginTop: '8px' }}>
                {archivedGoals.map(goal => (
                  <div key={goal.id} onClick={() => openEditGoal(goal)} style={{ padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer', opacity: 0.6 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div style={{ fontSize: '14px', fontWeight: 600, color: colors.text }}>{goal.name}</div>
                      <div style={{ fontSize: '12px', color: colors.muted }}>Archived</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Credit cards */}
        {!basicMode && creditCards.length > 0 && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', marginTop: '6px' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text }}>CREDIT CARDS</div>
            </div>
            <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', overflow: 'hidden', backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur, marginBottom: '16px' }}>
              {creditCards.map(card => {
                const owed = card.balance || 0
                const limit = card.credit_limit || 0
                const utilPct = limit > 0 ? Math.min(100, Math.round(owed / limit * 100)) : null
                const utilColor = utilPct > 75 ? colors.red : utilPct > 40 ? colors.gold : colors.green
                return (
                  <div key={card.id} style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: utilPct !== null ? '10px' : '12px' }}>
                      <BillLogo name={card.name} category="credit_card" size={42} radius={21} showCR />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: '15px', fontWeight: 700, color: colors.text }}>{card.name}</div>
                        <div style={{ fontSize: '13px', color: colors.muted, marginTop: '2px' }}>
                          {limit > 0 ? `${fmt(limit)} limit${utilPct !== null ? ` · ${utilPct}% used` : ''}` : 'Credit card'}
                        </div>
                      </div>
                      <div style={{ fontFamily: fonts.heading, fontSize: '20px', fontWeight: 800, color: colors.red }}>{fmt(owed)}</div>
                    </div>
                    {utilPct !== null && (
                      <div style={{ height: '4px', background: 'rgba(255,255,255,0.08)', borderRadius: '2px', marginBottom: '12px' }}>
                        <div style={{ height: '100%', width: `${utilPct}%`, background: utilColor, borderRadius: '2px' }} />
                      </div>
                    )}
                    <button onClick={() => openPayModal(card)} style={{ width: '100%', padding: '10px', background: 'rgba(201,151,43,0.1)', border: `1px solid rgba(201,151,43,0.3)`, borderRadius: '10px', color: colors.gold, fontFamily: fonts.body, fontSize: '14px', fontWeight: 700, cursor: 'pointer' }}>
                      Pay statement
                    </button>
                  </div>
                )
              })}
            </div>
          </>
        )}

        {/* Debt goals */}
        {!basicMode && <>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px', marginTop: '6px' }}>
          <div style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '0.06em', color: colors.text }}>DEBT GOALS</div>
        </div>

        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '16px', overflow: 'hidden', backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur, marginBottom: '16px' }}>
          {activeDebts.length === 0 ? (
            <div style={{ padding: '28px 20px', textAlign: 'center' }}>
              <div style={{ fontSize: '28px', marginBottom: '10px' }}>✅</div>
              <div style={{ fontSize: '14px', fontWeight: 500, color: colors.muted, lineHeight: 1.6 }}>No active debts - or add one to track payoff progress.</div>
            </div>
          ) : activeDebts.map(debt => {
            const paid = (debt.original_balance || 0) - (debt.current_balance || 0)
            const pct = debt.original_balance > 0 ? Math.round((paid / debt.original_balance) * 100) : 0
            return (
              <div key={debt.id} onClick={() => openEditDebt(debt)} style={{ padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '10px' }}>
                  <IC emoji="💳" color={colors.red} size={48} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '15px', fontWeight: 700, color: colors.text }}>{debt.name}</div>
                    <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '2px' }}>{debt.lender || ''}{debt.apr > 0 ? ` · ${debt.apr}% APR` : ''}</div>
                  </div>
                  <div style={{ fontFamily: fonts.heading, fontSize: '24px', fontWeight: 800, color: colors.red }}>{pct}%</div>
                  <div style={{ fontSize: '16px', color: `rgba(${colors.textRgb},0.3)` }}>›</div>
                </div>
                <ProgressBar pct={pct} color={colors.red} />
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '6px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted }}>{fmt(paid)} paid</div>
                  <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted }}>{fmt(debt.current_balance || 0)} remaining</div>
                </div>
              </div>
            )
          })}
          <div style={{ padding: '12px 16px' }}>
            <button onClick={openAddDebt} style={{ width: '100%', padding: '12px', background: 'transparent', border: '1px dashed rgba(224,82,82,0.3)', borderRadius: '10px', color: colors.red, fontFamily: fonts.body, fontSize: '14px', fontWeight: 600, cursor: 'pointer', opacity: 0.8 }}>
              + Add debt goal
            </button>
          </div>
        </div>

        </>}

        {/* Coaching tip */}
        <div style={{ background: colors.card, border: `1px solid ${colors.border}`, borderRadius: '14px', padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '14px', backdropFilter: colors.cardBlur, WebkitBackdropFilter: colors.cardBlur }}>
          <div style={{ width: '48px', height: '48px', borderRadius: '50%', background: 'rgba(201,151,43,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', flexShrink: 0 }}>💡</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: colors.text }}>Stay consistent</div>
            <div style={{ fontSize: '13px', fontWeight: 500, color: colors.muted, marginTop: '2px', lineHeight: 1.5 }}>Small, regular contributions make the biggest difference over time.</div>
          </div>
        </div>

      </div>

      {/* Goal modal */}
      {showGoalModal && (
        <Modal title={editingGoal ? 'Edit goal' : 'Add savings goal'} onClose={() => setShowGoalModal(false)}>
          <Input label="Goal name" value={goalForm.name} onChange={v => setGoalForm(f => ({ ...f, name: v }))} placeholder="e.g. Emergency Fund" />
          <Input label="Target amount" type="number" prefix="£" value={goalForm.target_amount} onChange={v => setGoalForm(f => ({ ...f, target_amount: v }))} placeholder="3000" />
          <Select label="Link to savings account (optional)" value={goalForm.linked_account_id} onChange={v => setGoalForm(f => ({ ...f, linked_account_id: v }))} options={accountOptions} />
          {!goalForm.linked_account_id && (
            <Input label="Current amount" type="number" prefix="£" value={goalForm.current_amount} onChange={v => setGoalForm(f => ({ ...f, current_amount: v }))} placeholder="0" />
          )}
          {goalForm.linked_account_id && (
            <div style={{ fontSize: '13px', color: colors.green, padding: '8px 12px', background: 'rgba(61,184,122,0.08)', borderRadius: '8px', marginBottom: '10px' }}>
              Current amount will sync from the linked account balance automatically.
            </div>
          )}
          <Input label="Monthly contribution" type="number" prefix="£" value={goalForm.monthly_contribution} onChange={v => setGoalForm(f => ({ ...f, monthly_contribution: v }))} placeholder="0" />
          {parseFloat(goalForm.monthly_contribution) > 0 && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px', fontSize: '13px', fontWeight: 600, color: colors.muted }}>
                Fund from account <Hint title="Fund from account" text="The current account your monthly contribution is automatically transferred from at the start of each period. Without this set, the contribution reduces Safe to Spend but money won't move automatically." />
              </div>
              <Select
                value={goalForm.source_account_id}
                onChange={v => setGoalForm(f => ({ ...f, source_account_id: v }))}
                options={[
                  { value: '', label: 'No account (manual)' },
                  ...accounts.filter(a => a.type === 'current').map(a => ({ value: a.id, label: `${a.name} - ${fmt(a.balance)}` }))
                ]}
              />
            </div>
          )}
          {goalError && <div style={{ fontSize: '13px', color: colors.red, padding: '8px 12px', background: 'rgba(224,82,82,0.1)', borderRadius: '8px', marginBottom: '10px' }}>{goalError}</div>}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', marginTop: '8px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              {editingGoal && <GhostButton danger onClick={() => { deleteGoal(editingGoal.id); setShowGoalModal(false) }}>Delete</GhostButton>}
              {editingGoal && editingGoal.current_amount >= editingGoal.target_amount && editingGoal.target_amount > 0 && (
                <GhostButton onClick={handleArchiveGoal}>Archive</GhostButton>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
              <GhostButton onClick={() => setShowGoalModal(false)}>Cancel</GhostButton>
              <GoldButton onClick={handleSaveGoal}>{savingGoal ? 'Saving...' : 'Save'}</GoldButton>
            </div>
          </div>
        </Modal>
      )}

      {/* Debt modal */}
      {showDebtModal && (
        <Modal title={editingDebt ? 'Edit debt' : 'Add debt'} onClose={() => setShowDebtModal(false)}>
          <Input label="Debt name" value={debtForm.name} onChange={v => setDebtForm(f => ({ ...f, name: v }))} placeholder="e.g. Credit Card" />
          <Input label="Lender" value={debtForm.lender} onChange={v => setDebtForm(f => ({ ...f, lender: v }))} placeholder="e.g. Barclays" />
          <Input label="Original balance" type="number" prefix="£" value={debtForm.original_balance} onChange={v => setDebtForm(f => ({ ...f, original_balance: v }))} placeholder="5000" />
          <Input label="Current balance" type="number" prefix="£" value={debtForm.current_balance} onChange={v => setDebtForm(f => ({ ...f, current_balance: v }))} placeholder="4200" />
          <Input label="APR %" type="number" value={debtForm.apr} onChange={v => setDebtForm(f => ({ ...f, apr: v }))} placeholder="21.9" />
          <Input label="Monthly payment" type="number" prefix="£" value={debtForm.monthly_payment} onChange={v => setDebtForm(f => ({ ...f, monthly_payment: v }))} placeholder="150" />
          {debtError && <div style={{ fontSize: '13px', color: colors.red, padding: '8px 12px', background: 'rgba(224,82,82,0.1)', borderRadius: '8px', marginBottom: '10px' }}>{debtError}</div>}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', marginTop: '8px' }}>
            {editingDebt && <GhostButton danger onClick={() => { deleteDebt(editingDebt.id); setShowDebtModal(false) }}>Delete</GhostButton>}
            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
              <GhostButton onClick={() => setShowDebtModal(false)}>Cancel</GhostButton>
              <GoldButton onClick={handleSaveDebt}>{savingDebt ? 'Saving...' : 'Save'}</GoldButton>
            </div>
          </div>
        </Modal>
      )}

      {showPayModal && payingCard && (
        <Modal title={`Pay ${payingCard.name}`} onClose={() => { setShowPayModal(false); setPayingCard(null) }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 0', borderBottom: '1px solid rgba(255,255,255,0.07)', marginBottom: '12px' }}>
            <div>
              <div style={{ fontSize: '14px', fontWeight: 600, color: colors.text }}>Clear full balance</div>
              <div style={{ fontSize: '12px', color: colors.muted, marginTop: '2px' }}>Pay off {fmt(payingCard.balance || 0)}</div>
            </div>
            <button onClick={() => setPayForm(f => ({ ...f, clear_all: !f.clear_all, amount: !f.clear_all ? String(payingCard.balance || '') : '' }))} style={{ width: '44px', height: '26px', borderRadius: '13px', border: 'none', cursor: 'pointer', background: payForm.clear_all ? colors.gold : 'rgba(255,255,255,0.15)', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
              <div style={{ width: '20px', height: '20px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px', left: payForm.clear_all ? '21px' : '3px', transition: 'left 0.2s' }} />
            </button>
          </div>
          {!payForm.clear_all && <Input label="Amount to pay" type="number" prefix="£" value={payForm.amount} onChange={v => setPayForm(f => ({ ...f, amount: v }))} placeholder="0.00" />}
          {accounts.filter(a => a.type === 'current').length > 0 && (
            <Select label="Pay from account" value={payForm.account_id} onChange={v => setPayForm(f => ({ ...f, account_id: v }))} options={[{ value: '', label: 'No account (manual)' }, ...accounts.filter(a => a.type === 'current').map(a => ({ value: a.id, label: `${a.name} - ${fmt(a.balance)}` }))]} />
          )}
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '8px' }}>
            <GhostButton onClick={() => { setShowPayModal(false); setPayingCard(null) }}>Cancel</GhostButton>
            <GoldButton onClick={handlePayStatement}>{payingSaving ? 'Saving...' : 'Confirm payment'}</GoldButton>
          </div>
        </Modal>
      )}
    </div>
  )
}
